import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { normalizeAuthEmail } from '../../hooks/authInputUtils.js';
import Logo from '../Logo';

export default function Login({ mode = 'signin', onSwitchMode, onBackHome, onForgotPassword }) {
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [showInlineReset, setShowInlineReset] = useState(false);
  const { signIn, signUp, requestPasswordReset } = useAuth();
  const isSignUp = mode === 'signup';

  const mapAuthErrorMessage = (rawMessage) => {
    const message = String(rawMessage || '');

    if (/invalid login credentials/i.test(message)) {
      return 'We could not sign you in. Re-enter your password manually, then try "Forgot password?" if needed.';
    }

    return message || 'Authentication failed. Please try again.';
  };

  useEffect(() => {
    setError(null);
    setMessage(null);
    setShowInlineReset(false);
  }, [mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setShowInlineReset(false);

    const normalizedEmail = normalizeAuthEmail(email);
    if (!normalizedEmail) {
      setError('Please enter your email address.');
      return;
    }
    if (!password) {
      setError('Please enter your password.');
      return;
    }

    setLoading(true);

    try {
      if (isSignUp) {
        const { error } = await signUp(normalizedEmail, password, displayName);
        if (error) throw error;
        setMessage('Check your email for the confirmation link!');
        setEmail('');
        setPassword('');
      } else {
        const { error } = await signIn(normalizedEmail, password);
        if (error) throw error;
      }
    } catch (error) {
      const rawMessage = String(error?.message || '');
      if (!isSignUp && /invalid login credentials/i.test(rawMessage)) {
        setShowInlineReset(true);
      }
      setError(mapAuthErrorMessage(rawMessage));
    } finally {
      setLoading(false);
    }
  };

  const handleInlineReset = async () => {
    setError(null);
    setMessage(null);
    setShowInlineReset(false);

    const normalizedEmail = normalizeAuthEmail(email);
    if (!normalizedEmail) {
      setError('Enter your email first, then request a password reset link.');
      return;
    }

    setLoading(true);
    try {
      const redirectTo = `${window.location.origin}/reset-password`;
      const { error } = await requestPasswordReset(normalizedEmail, redirectTo);
      if (error) throw error;
      setMessage('Password reset email sent. Open it on this device and set a new password.');
    } catch (error) {
      setError(String(error?.message || 'Could not send password reset email. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="auth-page">
      <div className="auth-shell">
        <section className="auth-visual">
          <button type="button" className="auth-back-link" onClick={onBackHome}>
            Back to home
          </button>
          <div className="auth-brand">
            <div className="auth-brand-mark">
              <Logo width={28} height={28} />
            </div>
            <span>GoHalfsies</span>
          </div>
          <h1>Shared money, clear decisions.</h1>
          <p>
            Track expenses together, keep a running balance, and settle up with full transparency.
          </p>
          <ul>
            <li>Invite your partner with a code</li>
            <li>Log expenses in seconds</li>
            <li>See exactly who owes what</li>
          </ul>
        </section>

        <section className="auth-form-card">
          <p className="auth-eyebrow">{isSignUp ? 'Create account' : 'Welcome back'}</p>
          <h2>{isSignUp ? 'Start your shared workspace' : 'Sign in to your workspace'}</h2>
          <p className="auth-subhead">
            {isSignUp ? 'Use email and password to create your account.' : 'Use your email and password to continue.'}
          </p>

          {error && (
            <div className="auth-error">
              <p style={{ margin: 0 }}>{error}</p>
              {showInlineReset && (
                <button
                  type="button"
                  className="auth-switch-btn"
                  onClick={handleInlineReset}
                  disabled={loading}
                  style={{ marginTop: '8px' }}
                >
                  Send password reset email now
                </button>
              )}
            </div>
          )}

          {message ? (
            <div className="auth-message-card">
              <p>{message}</p>
              <button
                type="button"
                className="submit-btn"
                onClick={() => onSwitchMode?.('signin')}
                style={{ marginTop: '12px' }}
              >
                Go to login
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              {isSignUp && (
                <div className="form-group">
                  <label>Display Name</label>
                  <input
                    type="text"
                    name="displayName"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    required
                    placeholder="e.g. Tom"
                    disabled={loading}
                  />
                </div>
              )}

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete={isSignUp ? 'email' : 'username'}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  inputMode="email"
                  placeholder="name@example.com"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={isSignUp ? 'new-password' : 'current-password'}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  placeholder="••••••••"
                  disabled={loading}
                />
              </div>

              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? 'Loading...' : (isSignUp ? 'Create account' : 'Log in')}
              </button>

              {!isSignUp && (
                <div className="auth-forgot-row">
                  <button type="button" className="auth-switch-btn" onClick={onForgotPassword}>
                    Forgot password?
                  </button>
                </div>
              )}

              <div className="auth-switch-row">
                <span>{isSignUp ? 'Already have an account?' : 'Need an account?'}</span>
                <button
                  type="button"
                  className="auth-switch-btn"
                  onClick={() => onSwitchMode?.(isSignUp ? 'signin' : 'signup')}
                >
                  {isSignUp ? 'Log in' : 'Create account'}
                </button>
              </div>
            </form>
          )}
        </section>
      </div>
    </main>
  );
}
